import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

function StatCard({ label, value, suffix, highlight }) {
  return (
    <div className="border rounded-lg p-5" style={{ backgroundColor: '#0d2818', borderColor: highlight ? '#c9a961' : 'rgba(201,169,97,0.3)' }}>
      <p className="text-body-xs mb-2" style={{ color: '#999999' }}>{label}</p>
      <p className="text-headline-md font-bold" style={{ color: '#c9a961' }}>
        {value}{suffix || ''}
      </p>
    </div>
  );
}

function fmt(n) {
  return Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function AdminStats() {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    if (!isAdmin) {
      navigate('/dashboard');
    }
  }, [authLoading, user, isAdmin, navigate]);

  useEffect(() => {
    if (!user || !isAdmin) return;

    const fetchStats = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/dashboard-stats`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (!res.ok || !data.success) {
          throw new Error(data.error || 'Failed to load dashboard stats');
        }
        setStats(data.stats);
      } catch (err) {
        setError(err.message || 'Failed to load dashboard stats');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token]);

  if (!user || !isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen px-4 pt-24 pb-32" style={{ backgroundColor: '#1a3a2a' }}>
    <div className="max-w-5xl mx-auto">
      <h1 className="text-headline-lg font-bold mb-2" style={{ color: '#c9a961' }}>
        Admin Dashboard
      </h1>
      <p className="text-body-sm mb-8" style={{ color: '#999999' }}>
        Company-wide point balances, sales, and payout totals
      </p>

      {error && (
        <div className="border rounded-lg p-4 mb-6" style={{ backgroundColor: '#2a0d0d', borderColor: '#c94961', color: '#c94961' }}>
          {error}
        </div>
      )}

      {loading ? (
        <p style={{ color: '#b8a68f' }}>Loading...</p>
      ) : stats ? (
        <>
          <h2 className="text-headline-sm font-bold mb-4" style={{ color: '#c9a961' }}>Point Balances (company-wide)</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard label="Total Credit Points" value={fmt(stats.total_credit_points)} highlight />
            <StatCard label="Total Cash Points" value={fmt(stats.total_cash_points)} highlight />
            <StatCard label="Total Product Points" value={fmt(stats.total_product_points)} highlight />
            <StatCard label="Total Repurchased Credit Points" value={fmt(stats.total_repurchased_credit_points)} highlight />
          </div>

          <h2 className="text-headline-sm font-bold mb-4" style={{ color: '#c9a961' }}>Members</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            <StatCard label="Total Members" value={stats.total_members} />
            <StatCard label="Verified Members" value={stats.verified_members} />
            <StatCard label="Pending Approvals" value={stats.pending_approvals} />
          </div>

          <h2 className="text-headline-sm font-bold mb-4" style={{ color: '#c9a961' }}>Sales</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            <StatCard label="Total Sales (all time)" value={fmt(stats.total_sales)} suffix=" CP" />
            <StatCard label="Sales This Month" value={fmt(stats.sales_this_month)} suffix=" CP" />
            <StatCard label="Confirmed Orders" value={stats.orders_confirmed} />
          </div>

          <h2 className="text-headline-sm font-bold mb-4" style={{ color: '#c9a961' }}>Payouts Released</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Welcome Bonus Paid" value={fmt(stats.total_welcome_bonus_paid)} />
            <StatCard label="Direct Referral Released" value={fmt(stats.total_direct_referral_released)} />
            <StatCard label="Team Incentives Released" value={fmt(stats.total_team_incentives_released)} />
            <StatCard label="Passive Released" value={fmt(stats.total_company_pool_released)} />
          </div>
        </>
      ) : null}
    </div>
    </div>
  );
}
